import { Flex, Spacing, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'

import { ModalId } from '../../../../../../enums/modalId'
import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { canDeleteFecImports } from '../../utils/canDeleteFecImports'
import { FecImportConfirmationActions } from './elements/FecImportConfirmationActions'
import { FecImportConfirmationContent } from './elements/FecImportConfirmationContent'
import { FecImportSkipFailedFileModal } from './elements/FecImportSkipFailedFileModal'
import * as Styled from './styles'

export const FecImportConfirmationStep = (): ReactElement | null => {
  const {
    canRetryAnalysis,
    confirmationKind,
    isReuploading,
    isRetryingAnalysis,
    onRetryAnalysis,
    onReupload,
    onSkipFailedFileContinue,
    onStartMappingReview,
    sessionFecImports,
    sessionFiles,
  } = useFecImportSessionContext()
  const { open: openSkipFailedFileModal } = useModal(ModalId.FecImportSkipFailedFileModal)

  const handleReviewMappingsClick = useCallback(() => {
    if (confirmationKind === 'partial') {
      openSkipFailedFileModal()
      return
    }

    onStartMappingReview()
  }, [confirmationKind, onStartMappingReview, openSkipFailedFileModal])

  const handleSkipFailedFileContinue = useCallback(async () => {
    await onSkipFailedFileContinue()
    onStartMappingReview()
  }, [onSkipFailedFileContinue, onStartMappingReview])

  if (!confirmationKind) {
    return null
  }

  return (
    <Styled.ConfirmationContainer>
      <Flex flexDirection="column" gap={Spacing.S20}>
        <FecImportConfirmationContent
          confirmationKind={confirmationKind}
          fecImports={sessionFecImports}
          sessionFiles={sessionFiles}
        />
        <FecImportConfirmationActions
          canDelete={canDeleteFecImports(sessionFecImports)}
          canRetryAnalysis={canRetryAnalysis}
          confirmationKind={confirmationKind}
          isReuploading={isReuploading}
          isRetryingAnalysis={isRetryingAnalysis}
          onRetryAnalysis={onRetryAnalysis}
          onReupload={onReupload}
          onReviewMappings={handleReviewMappingsClick}
        />
      </Flex>
      {confirmationKind === 'partial' && (
        <FecImportSkipFailedFileModal
          okLoading={isReuploading}
          onContinue={handleSkipFailedFileContinue}
          onReupload={onReupload}
        />
      )}
    </Styled.ConfirmationContainer>
  )
}
